// src/context/WorkspaceConfigContext.jsx
import React, { createContext, useContext, useEffect, useMemo } from "react";
import { useQuery } from "react-query";
import { fetchData } from "../api/client";
import { useEffectivePermissions } from "../hooks/useEffectivePermissions";
import { useAuthContext } from "../context/AuthContext";
import {
  CONFIG_SECRET_SENTINEL,
  isSecretConfigKeyName,
  isSecretJsonConfigKeyName,
} from "../constants/passagensConfigHelpers";
import {
  readActiveCompanySession,
  readSelectedCompanySession,
  writeActiveCompanySession,
  readWorkspaceSession,
  writeWorkspaceSession,
  getActiveWorkspaceSlug,
  WS_KEY,
} from "../utils/workspaceStorage";

/**
 * A API já devolve `CONFIG_SECRET_SENTINEL` em vez de senha/token de ERP, mas
 * este contexto persiste a resposta em localStorage — que fica em disco no
 * navegador de todo usuário. Então nenhum `value` de item marcado como `secret`
 * passa daqui, independente do que a API tenha devolvido.
 */
function stripSecretValues(data) {
  const items = Array.isArray(data?.items) ? data.items : null;
  if (!items) return data;
  return {
    ...data,
    items: items.map((item) => {
      const key = item?.key;
      if (isSecretConfigKeyName(key)) {
        const configured =
          item?.configured != null
            ? !!item.configured
            : item?.value != null && String(item.value).trim() !== "";
        return {
          ...item,
          secret: true,
          configured,
          value: configured ? CONFIG_SECRET_SENTINEL : "",
        };
      }
      // JSON com credencial nas folhas: só o servidor sabe higienizar por folha,
      // então cache não redigido é descartado em vez de mantido.
      if (isSecretJsonConfigKeyName(key) && !item?.secret) {
        return { ...item, secret: true, configured: false, value: "" };
      }
      return item;
    }),
  };
}

/** Lê empresa ativa do storage (prioridade: pin da aba). */
function getStoredCompanyKey() {
  try {
    return (
      getActiveWorkspaceSlug() ||
      readActiveCompanySession() ||
      readSelectedCompanySession() ||
      null
    );
  } catch {
    return null;
  }
}

const WorkspaceConfigContext = createContext({
  isLoading: true,
  isError: false,
  items: [],
  byKey: () => undefined,
  raw: null,
  refetch: () => {},
  companyKey: null,
});

export function WorkspaceConfigProvider({ children }) {
  const { bearerToken } = useAuthContext?.() || {};
  // Aproveita o mesmo endpoint já usado no <Require />
  const { data: permData, isLoading: permsLoading } = useEffectivePermissions();

  // 1) Decide a companyKey: storage -> /me/permissions.company
  const stored = getStoredCompanyKey();
  const companyFromPerms = permData?.company ? String(permData.company) : null;
  const companyKey = stored || companyFromPerms || null;

  // 2) Se vier company das permissões e não houver no storage ainda, grava para próximas telas
  useEffect(() => {
    if (!stored && companyFromPerms) {
      try {
        writeActiveCompanySession(String(companyFromPerms));
      } catch {}
    }
  }, [stored, companyFromPerms]);

  const query = useQuery(
    ["workspace-config", companyKey],
    async () => {
      // GET /configurations (o fetchData deve enviar o header Company automaticamente)
      const res = await fetchData(`/configurations`, "GET");
      const data = res?.data || res || {};
      try {
        writeWorkspaceSession(
          WS_KEY.WORKSPACE_CONFIG_CACHE,
          JSON.stringify(stripSecretValues(data))
        );
      } catch {}
      return stripSecretValues(data);
    },
    {
      enabled: !!bearerToken && !!companyKey && !permsLoading,
      refetchOnWindowFocus: false,
      staleTime: 60_000,
      initialData: () => {
        try {
          const raw = readWorkspaceSession(WS_KEY.WORKSPACE_CONFIG_CACHE);
          if (!raw) return undefined;
          // Cache gravado antes desta correção pode ter credencial em claro.
          const sanitized = stripSecretValues(JSON.parse(raw));
          writeWorkspaceSession(WS_KEY.WORKSPACE_CONFIG_CACHE, JSON.stringify(sanitized));
          return sanitized;
        } catch {
          return undefined;
        }
      },
    }
  );
  
  const value = useMemo(() => {
    const items = Array.isArray(query.data?.items) ? query.data.items : [];
    const map = new Map(items.map((i) => [i.key, i]));
    const byKey = (key, parseJson = false, fallback = undefined) => {
      if (!key) return fallback;
      const hit = map.get(key);
      if (!hit) return fallback;
      if (parseJson) {
        try {
          return JSON.parse(hit.value);
        } catch {
          return fallback;
        }
      }
      return hit.value;
    };

    return {
      isLoading: permsLoading || query.isLoading || query.isFetching,
      isError: !!query.error,
      items,
      byKey,
      raw: query.data || null,
      refetch: query.refetch,
      companyKey,
    };
  }, [
    permsLoading,
    query.isLoading,
    query.isFetching,
    query.error,
    query.data,
    query.refetch,
    companyKey,
  ]);

  return (
    <WorkspaceConfigContext.Provider value={value}>
      {children}
    </WorkspaceConfigContext.Provider>
  );
}

export function useWorkspaceConfig() {
  return useContext(WorkspaceConfigContext);
}
